import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'app/main/rootReducer';
import { getComparator, stableSort } from './SortTableHelper';
import { AppendDollarToAmount, MakeBookingDetails } from './helper';
import { Order, IBookingDetails, IBookingDetailsTableHeader } from './types';
import { makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel } from '@material-ui/core';

const useStyles = makeStyles({
  root: {
    '& .MuiTableCell-head': {
      fontWeight: 'bold'
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: '48px'
    },
    '& .MuiTableCell-root': {
      padding: '12px 6px'
    },
    width: '500px'
  },
  container: {
    minHeight: '400px'
  },
  darkBlueColour: {
    color: 'darkblue'
  },
  crimsonColour: {
    color: 'crimson'
  },
  darkGreenColour: {
    color: 'darkgreen'
  },
  tableTitle: {
    fontWeight: 'normal',
    margin: '8px'
  },
  tagsFontSize: {
    fontSize: '16px'
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  }
});

/** Table header */
const headerCells: IBookingDetailsTableHeader[] = [
  { id: 'fullName', label: 'Name & Company', minWidth: 150 },
  { id: 'averageAmountInDollar', label: 'PH Spend', minWidth: 60 },
  { id: 'totalAmountInDollar', label: 'Total', minWidth: 60 },
  { id: 'tags', label: 'Tags', minWidth: 60 },
  { id: 'customerVisitCount', label: 'Visits', minWidth: 40 }
];

/**
 * Displays the list of today's customers in tabular format
 */
export const CustomersTable = () => {

  const rowsPerPage = 10;
  const classes = useStyles();
  const [page, setPage] = useState<number>(0);
  const [order, setOrder] = useState<Order>('desc');
  const [rows, setTableRows] = useState<IBookingDetails[]>([]);
  const [orderBy, setOrderBy] = useState<keyof IBookingDetails>('averageAmountInDollar');
  const { filteredBookingList } = useSelector((state: RootState) => state.venueReducer);

  useEffect(() => {
    let active = true;
    if (active && (filteredBookingList?.length ?? 0) > 0) {
      const dataRows = filteredBookingList
        .filter(f => f.status.statusType.toLowerCase() !== "cancelled")
        .map(booking => MakeBookingDetails(booking));
      
        // Sort the bookings by taking the entire customer list into consideration
      const customers = stableSort(dataRows, getComparator(order, orderBy)).slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage)
        .reduce((list: IBookingDetails[], current: IBookingDetails) => { 
          if (!list.find(f => f.customerId === current.customerId)) { 
            list.push(current); 
          } 
          return list; 
        }, [] as IBookingDetails[]);

      setTableRows(customers);
    }
    return () => { active = false; }
  }, [filteredBookingList, page, order]);

  // Navigate to the first page, whenever data is refreshed.
  useEffect(() => setPage(0), [filteredBookingList]);

  if (filteredBookingList.length === 0) return null;

  const onPageChanged = (event: unknown, newPage: number) => {
    setPage(newPage);
  }

  /** Sort the column in asc/desc order */
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IBookingDetails) => {
    /**
     * isAsc is false, when a new column is selected for sorting in ascending order.
     * isAsc is true, when the same column is selected again for sorting but, this time in descending order.
     */
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filters the rows as per the specified page number
  const columns = rows
  .map((booking, index) => {
    return (
      <TableRow key={index}>
        <TableCell>{booking.fullName}</TableCell>
        <TableCell>{AppendDollarToAmount(booking.averageAmountInDollar)}</TableCell>
        <TableCell>{AppendDollarToAmount(booking.totalAmountInDollar)}</TableCell>
        <TableCell className={classes.tagsFontSize}>
          {
            booking.tags.split(',').map((tag,index) => {
              return (
                <span key={index}>
                  {tag === "VIP" && <span className={classes.darkBlueColour}>&#9733;</span>}
                  {tag === "Previous No Show" && <span className={classes.crimsonColour}>&nbsp;&#10006;</span>}
                </span>
              )
            })
          }
        </TableCell>
        <TableCell>
          {
            booking.customerVisitCount > 1 &&
            <>
              <span className={classes.darkGreenColour}>{booking.customerVisitCount}</span>
              <span className={classes.darkGreenColour}>&nbsp;&#x27F3;</span>
            </>
          }
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Paper id="todaysGuestTable" className={classes.root}>
      <h2 className={classes.tableTitle}>Guests</h2>
      <TableContainer className={classes.container}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
            {
                headerCells.map(header => (
                  <TableCell key={header.id} sortDirection={orderBy === header.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === header.id}
                      direction={orderBy === header.id ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, header.id)}>
                      {header.label}
                      {
                        orderBy === header.id ? (
                          <span className={classes.visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </span>
                        ) : null
                      }
                    </TableSortLabel>
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {columns}
          </TableBody>
        </Table>
      </TableContainer>
      {
        rows.length > 0 &&
        <TablePagination
          page={page}
          component="div"
          labelRowsPerPage=""
          rowsPerPageOptions={[]}
          rowsPerPage={rowsPerPage}
          count={rows.length}
          onChangePage={onPageChanged} />
      }
    </Paper>
  )
}
